/* eslint-disable @/no-restricted-imports */
import React, { forwardRef, useMemo, CSSProperties, memo } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
} from 'react-hook-form';
import {
  AutocompleteFreeSoloValueMapping,
  Box,
  Divider,
  List,
  Paper,
  PaperProps,
  PopperProps,
  ThemeProvider,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import {
  areEqual,
  ListChildComponentProps,
  VariableSizeList,
} from 'react-window';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete, {
  AutocompleteChangeReason,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import {
  error as errorColors,
  other,
  primary,
  secondary,
} from 'assets/colors/colors';
import AddIcon from '@mui/icons-material/Add';
import { createMuiAutocompleteTheme } from 'theme';
import { RenderChips, RenderTags } from './components/RenderTags/RenderTags';
import { MuiAutocompleteProps } from './types/types';
import { RenderOption } from './components/RenderOption';
import { StyledPopper } from './styles/styles';
import { Button } from 'components';

const ITEM_HEIGHT = 42;
const MAX_HEIGHT = 300;

interface ListboxComponentProps extends React.HTMLAttributes<HTMLElement> {
  isGroupedOption?: boolean;
  multiple?: boolean;
}

const OuterElementContext = React.createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const getChildLength = (item: React.ReactElement) =>
  item?.props?.children?.[1]?.props?.children?.length || 0;

const calculateHeight = (
  index: number,
  childLength: number,
  isGroupedOption?: boolean,
  multiple?: boolean,
) => {
  if (isGroupedOption) {
    return index === 0
      ? 40 + childLength * (multiple ? 42 : 36)
      : 8 + childLength * (multiple ? 40 : 36);
  }
  return 40 + childLength * 44;
};

const ListboxComponent = forwardRef<HTMLDivElement, ListboxComponentProps>(
  (props, ref) => {
    const { children, isGroupedOption, multiple, ...other } = props;
    const itemData: React.ReactElement<unknown>[] = [];
    (children as React.ReactElement<unknown>[]).forEach(
      (
        item: React.ReactElement<unknown> & {
          children?: React.ReactElement<unknown>[];
        },
      ) => {
        itemData.push(item);
        if (item.children) {
          itemData.push(...item.children);
        }
      },
    );

    const getItemSize = (index: number) => {
      const item = itemData[index] as React.ReactElement;
      if (!item) {
        return ITEM_HEIGHT;
      }

      const childLength = getChildLength(item);
      const height = calculateHeight(
        index,
        childLength,
        isGroupedOption,
        multiple,
      );

      return height || ITEM_HEIGHT;
    };

    const itemCount = itemData.length;
    // Calculate the total height of the list dynamically
    const totalHeight = itemData.reduce(
      (sum, _, index) => sum + getItemSize(index),
      0,
    );
    const listHeight = Math.min(totalHeight, MAX_HEIGHT);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={listHeight}
            width="100%"
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={getItemSize}
            overscanCount={5}
            itemCount={itemCount}
          >
            {Row}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  },
);

const Row = memo((props: ListChildComponentProps) => {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle: CSSProperties = {
    ...style,
    top: (style.top as number) + 4,
    bottom: (style.top as number) + 4,
  };

  return <div style={inlineStyle}>{dataSet}</div>;
}, areEqual);

export default function VirtualizedAutoComplete<
  T extends FieldValues,
  O,
  Multiple extends boolean = false,
>({
  id,
  optionData = [],
  placeholder,
  datatestid,
  setValue,
  getOptionLabel,
  getOptionKey,
  getOptionGroup,
  multiple = false as Multiple,
  label,
  value,
  loading,
  disableClearable,
  width,
  disablePortal,
  control,
  style,
  name,
  error,
  disabled = false,
  createable = false,
  createLabel,
  showErrorMessage = false,
  creatableOnClick,
  chipTag = false,
  limitTags = 1,
  showEquipmentCustomLabel = false,
  showLibraryResourceCustomLabel = false,
  onBlur,
  onDeleteChip,
  customSelectedLabel = '',
  chipTagStyle,
  onInputChange,
  getOptionDisabled,
  fullWidthChips = false,
  popperPlacement = 'bottom-start',
  onOpen,
  onClose,
  isOptionEqualToValue,
  height,
}: MuiAutocompleteProps<T, O, Multiple>) {
  const theme = createMuiAutocompleteTheme({ width, multiple, height });
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: Multiple extends true ? O[] : O,
    _reason: AutocompleteChangeReason,
  ) => {
    setValue?.(newValue);
  };

  const listBoxProps = useMemo(
    () => ({
      multiple,
      style: {
        maxHeight: Math.min(optionData.length * ITEM_HEIGHT, MAX_HEIGHT),
      },
    }),
    [optionData.length, multiple],
  );

  const MemoizedPaper = useMemo(
    () => (props: PaperProps) => (
      <Paper>
        <List>{props.children}</List>
        {createable && (
          <>
            <Divider />
            <Button
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onMouseDown={creatableOnClick}
              fullWidth
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                padding: '8px',
                '&:hover': {
                  backgroundColor: secondary.background,
                },
              }}
            >
              {createLabel}
            </Button>
          </>
        )}
      </Paper>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createable, createLabel],
  );

  const isGroupedOption = useMemo(
    () => Boolean(getOptionGroup),
    [getOptionGroup],
  );

  const MemoizedListboxComponent = useMemo(
    () => (props: ListboxComponentProps) => (
      <ListboxComponent {...props} isGroupedOption={isGroupedOption} />
    ),
    [isGroupedOption],
  );

  const MemoizedPopper = useMemo(
    () => (props: PopperProps) => (
      <StyledPopper
        {...props}
        disablePortal={disablePortal}
        placement={popperPlacement}
        popperOptions={{
          placement: popperPlacement,
        }}
      />
    ),
    [popperPlacement, disablePortal],
  );

  const filterOptions = useMemo(
    () =>
      createFilterOptions<O>({
        trim: true,
      }),
    [],
  );

  const renderAutoComplete = (
    fieldProps?: ControllerRenderProps<T, Path<T>>,
    fieldState?: ControllerFieldState,
  ) => (
    <ThemeProvider theme={theme}>
      <Autocomplete
        limitTags={limitTags}
        multiple={multiple}
        id={id}
        disablePortal={disablePortal}
        value={value || fieldProps?.value}
        options={optionData || []}
        loading={loading}
        filterOptions={filterOptions}
        onOpen={onOpen}
        clearOnBlur
        onClose={onClose}
        disableCloseOnSelect={multiple}
        disableClearable={disableClearable}
        getOptionLabel={(option) => getOptionLabel(option as O)}
        getOptionDisabled={getOptionDisabled}
        getOptionKey={
          getOptionKey as (
            option?: O | AutocompleteFreeSoloValueMapping<Multiple>,
          ) => string
        }
        groupBy={getOptionGroup}
        onChange={(event, newValue, reason) => {
          handleChange(
            event,
            newValue as Multiple extends true ? O[] : O,
            reason,
          );
          if (fieldProps?.onChange) {
            fieldProps?.onChange(newValue);
          }
        }}
        onInputChange={(event, newInputValue) => {
          if (onInputChange) {
            onInputChange(event, newInputValue);
          }
        }}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              label={label}
              title={params?.inputProps?.value as string}
              data-testid={datatestid}
              placeholder={placeholder}
              onBlur={onBlur}
              error={!!fieldState?.error?.message || !!error}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  border:
                    fieldState?.error?.message || error
                      ? `1px solid ${errorColors.main} !important`
                      : `1px solid ${other.borderLight} !important`,
                },
                '& .MuiAutocomplete-clearIndicator': {
                  backgroundColor: primary.contrastText,
                  '&:hover': {
                    backgroundColor: `${primary.background} !important`,
                  },
                },
                ...(multiple && {
                  '& .Mui-error': {
                    color: `${errorColors?.main} !important`,
                  },
                }),

                ...(chipTag && {
                  '& .MuiOutlinedInput-root': {
                    height: 'auto !important',
                    minHeight: '40px !important',
                    flexDirection: 'row !important',
                    justifyContent: 'flex-start !important',
                    ...chipTagStyle,
                  },
                  '& .MuiInputBase-input.MuiAutocomplete-input.MuiAutocomplete-inputFocused':
                    {
                      opacity: '1 !important',
                      display: 'inline-block !important',
                      marginLeft: '8px !important',
                    },
                }),
                ...(disabled && {
                  '.Mui-disabled': {
                    background: other.grey,
                  },
                }),
                ...(error && {
                  '.MuiFormLabel-root': {
                    color: `${errorColors?.main} !important`,
                  },
                }),
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="secondary" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
            {!!fieldState?.error?.message && showErrorMessage && (
              <Typography
                variant="caption"
                color="error"
                display="block"
                data-testid={`form-${name}-input-error`}
              >
                {fieldState?.error?.message}
              </Typography>
            )}
          </>
        )}
        ListboxComponent={MemoizedListboxComponent}
        isOptionEqualToValue={
          isOptionEqualToValue ??
          ((option, value) =>
            (option as { id: string })?.id === (value as { id: string })?.id)
        }
        ListboxProps={listBoxProps}
        PopperComponent={MemoizedPopper}
        PaperComponent={MemoizedPaper}
        renderOption={(props, option, state) => (
          <RenderOption<O>
            props={props}
            option={option}
            state={state}
            getOptionLabel={getOptionLabel}
            multiple={multiple}
            showEquipmentCustomLabel={showEquipmentCustomLabel}
            showLibraryResourceCustomLabel={showLibraryResourceCustomLabel}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          chipTag ? (
            <Box my={1}>
              <RenderChips
                value={tagValue}
                getOptionLabel={getOptionLabel}
                getTagProps={getTagProps}
                onDeleteChip={onDeleteChip}
                limitTags={limitTags}
                fullWidthChips={fullWidthChips}
                parentWidth={width}
                style={{ maxWidth: '80%' }}
              />
            </Box>
          ) : (
            <RenderTags
              value={tagValue}
              getOptionLabel={getOptionLabel}
              customSelectedLabel={customSelectedLabel}
            />
          )
        }
        disabled={loading || disabled}
        style={style}
      />
    </ThemeProvider>
  );

  if (!control || !name) {
    return renderAutoComplete();
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => renderAutoComplete(field, fieldState)}
    />
  );
}
